/**
 * The landing screen.
 *
 * It is deliberately thin, and it says so — but it must say the *current*
 * true thing. Until 2026-09-07 it still read "return preparation and
 * reconciliation are in development", written when that was the case and left
 * standing after the parity build shipped GSTR-1, GSTR-3B, reconciliation,
 * prepared-versus-filed, reports and download. The sidebar carried all six
 * while this paragraph said none of them existed.
 *
 * Stale reassurance is worse than no reassurance: a practitioner who believes
 * it does not go looking for the screens, and a stakeholder reading it
 * concludes nothing has been built. What is genuinely absent is the filing
 * step and a real dashboard, and that is what it now says.
 */

import { ArrowRight, Building2, CalendarDays } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import { CLIENT, PERIOD, useEvaluation } from '../lib/access'
import { clients, periods } from '../lib/api'
import { summarise, summaryWords } from '../lib/calendar'

export function Overview() {
  const { can } = useEvaluation()
  const [count, setCount] = useState<number | null>(null)
  // The calendar's one line (FR-E7): one read of the same endpoint the Calendar screen makes.
  // `null` until it answers, and stays null if it cannot — a line that cannot be read says
  // nothing rather than a number.
  const [due, setDue] = useState<string | null>(null)

  useEffect(() => {
    if (!can(CLIENT, 'can_view')) return
    clients
      .list({ limit: 1 })
      .then((body) => setCount(Number(body.meta?.total ?? 0)))
      .catch(() => setCount(null))
  }, [can])

  useEffect(() => {
    if (!can(PERIOD, 'can_view')) return
    periods
      .obligations({ limit: 500 })
      .then((body) => setDue(summaryWords(summarise(body.data))))
      .catch(() => setDue(null))
  }, [can])

  return (
    <div>
      <h1 className="mb-1 text-xl font-semibold text-foreground">ProGST</h1>
      <p className="mb-8 text-sm text-muted-foreground">
        GST compliance for your practice — returns and input-credit reconciliation.
      </p>

      {can(CLIENT, 'can_view') && (
        <Link
          to="/clients"
          className="gst-card flex max-w-sm items-center gap-4 p-5 transition-colors hover:border-primary/40"
        >
          <Building2 className="text-primary" size={22} />
          <span className="flex-1">
            <span className="block text-sm font-medium text-foreground">Clients</span>
            <span className="block text-xs text-muted-foreground">
              {count === null ? 'Taxpayers you file for' : `${count} on your list`}
            </span>
          </span>
          <ArrowRight size={16} className="text-muted-foreground" />
        </Link>
      )}

      {can(PERIOD, 'can_view') && (
        <Link
          to="/calendar"
          className="gst-card mt-4 flex max-w-sm items-center gap-4 p-5 transition-colors hover:border-primary/40"
        >
          <CalendarDays className="text-primary" size={22} />
          <span className="flex-1">
            <span className="block text-sm font-medium text-foreground">Calendar</span>
            <span className="block text-xs text-muted-foreground" data-testid="overview-due">
              {due ?? 'Every client’s due dates'}
            </span>
          </span>
          <ArrowRight size={16} className="text-muted-foreground" />
        </Link>
      )}

      {/* The calendar line above is the first dashboard figure (FR-E7, 2026-10-08);
          there are still no tiles for 2B match rates. Left as an honest sentence
          rather than placeholder numbers: a fake number on a dashboard is
          believed, and the returns it would summarise are real now. */}
      <p className="mt-10 max-w-lg text-xs leading-relaxed text-muted-foreground">
        Preparation and reconciliation are here — GSTR-1, GSTR-3B, GSTR-2B matching and the
        prepared-versus-filed comparison, each reached from the sidebar.{' '}
        <strong className="font-medium text-foreground">Filing to the GST portal is not.</strong>{' '}
        Everything stops at a signed-off return and the files it produces, which you file
        yourself.
      </p>
    </div>
  )
}
